"use client"

import { RoleEnum } from "@/types/auth/roleEnum"
import {Code} from "lucide-react"
import React, { useMemo } from "react"
import { NavLink } from "react-router-dom"
import { useTranslation } from "react-i18next"

const publicNavItems = [
  {
    path: "/homepage",
    textKey: "nav.brandName",
    icon: (
        <Code className="w-5 h-5 mr-2 text-primary" />
    ),
  },
  {
    path: "/courses",
    textKey: "nav.courses",
    icon: (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="size-7"
        >
          <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5"
          />
        </svg>
    ),
  },
]

const memberNavItems = [
  {
    path: "/my-courses",
    textKey: "nav.myCourses",
    icon: (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-library-big-icon lucide-library-big"
        >
          <rect width="8" height="18" x="3" y="3" rx="1" />
          <path d="M7 3v18" />
          <path d="M20.4 18.9c.2.5-.1 1.1-.6 1.3l-1.9.7c-.5.2-1.1-.1-1.3-.6L11.1 5.1c-.2-.5.1-1.1.6-1.3l1.9-.7c.5-.2 1.1.1 1.3.6Z" />
        </svg>
    ),
  },
  {
    path: "/bookmarks",
    textKey: "nav.bookmarks",
    icon: (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-bookmark-icon lucide-bookmark"
        >
          <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
        </svg>
    ),
  },
  {
    path: "/certificates",
    textKey: "nav.certificates",
    icon: (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-award-icon lucide-award"
        >
          <circle cx="12" cy="8" r="6" />
          <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
        </svg>
    ),
  },
]

const lecturerNavItems = [
  {
    path: "/lecturer",
    textKey: "nav.lecturer",
    icon: (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-presentation-icon lucide-presentation"
        >
          <path d="M2 3h20" />
          <path d="M21 3v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V3" />
          <path d="m7 21 5-5 5 5" />
        </svg>
    ),
  },
]

const adminNavItems = [
  {
    path: "/admin-panel",
    textKey: "nav.adminPanel",
    icon: (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-shield-user-icon lucide-shield-user"
        >
          <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
          <path d="M6.376 18.91a6 6 0 0 1 11.249.003" />
          <circle cx="12" cy="11" r="4" />
        </svg>
    ),
  },
]

interface NavItemProps {
  path: string
  textKey: string
  icon: React.ReactElement
  isMobile?: boolean
  onNavigate?: () => void
}

const NavItem: React.FC<NavItemProps> = ({ path, textKey, icon, isMobile = false, onNavigate }) => {
    const { t } = useTranslation()
    const displayText = textKey ? t(textKey) : ""

    return (
    <NavLink
        to={path}
        onClick={() => {
          if (isMobile && onNavigate) {
            onNavigate()
          }
        }}
        className={({ isActive }) =>
            `flex gap-1 items-center px-3 py-2 transition-all duration-400 relative rounded-md ${
                isMobile ? "w-full justify-start" : ""
            } ${
                isActive
                    ? "text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20"
                    : "text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-50 dark:hover:bg-gray-800"
            }`
        }
    >
      {({ isActive }) => (
          <>
            <div className={`transition-colors duration-200 ${isActive ? "text-primary-600 dark:text-primary-400" : ""}`}>
              {icon}
            </div>
            {displayText && (
                <p
                    className={`text-base font-semibold transition-colors duration-200 ${
                        isActive ? "text-primary-600 dark:text-primary-400" : ""
                    }`}
                >
                  {displayText}
                </p>
            )}
            {isActive && !isMobile && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 dark:bg-primary-400 rounded-full" />
            )}
            {isActive && isMobile && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 dark:bg-primary-400 rounded-r-full" />
            )}
          </>
      )}
    </NavLink>
)}

type navbarProps = {
  userRoles: RoleEnum[] | null
  isMobile?: boolean
  onNavigate?: () => void
}

export default function Navbar({ userRoles, isMobile = false, onNavigate }: navbarProps) {
  const navItems = useMemo<NavItemProps[]>(() => {
    let finalNavItems = [...publicNavItems]
    if (userRoles) {
      finalNavItems = [...finalNavItems, ...memberNavItems]
      if (userRoles.includes(RoleEnum.Lecturer)) {
        finalNavItems = [...finalNavItems, ...lecturerNavItems]
      }
      if (userRoles.includes(RoleEnum.Admin)) {
        finalNavItems = [...finalNavItems, ...adminNavItems]
      }
    }
    return finalNavItems
  }, [userRoles])

  return (
      <div className={`flex gap-2 items-center ${isMobile ? "flex-col items-start w-full space-y-1" : "justify-center"}`}>
        {navItems.map((item) => (
            <React.Fragment key={item.path}>
              <NavItem {...item} isMobile={isMobile} onNavigate={onNavigate} />
            </React.Fragment>
        ))}
      </div>
  )
}
